/**
 * Enhanced API Hooks
 *
 * Provides React hooks for data fetching, state management, and error handling
 * with the new API service layer. Includes built-in async operation handling.
 *
 * Features:
 * - Automatic loading and error states
 * - Caching and cache invalidation
 * - Optimistic updates
 * - Real-time filtering and search
 * - CRUD operations with proper state management
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as api from "../services/api";

const DEFAULT_PARAMS = {};

/**
 * Generic API hook for CRUD operations
 */
export const useApiResource = (
  resourceType,
  initialParams = DEFAULT_PARAMS
) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState(initialParams);
  const [lastFetch, setLastFetch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug loading state changes
  useEffect(() => {
    console.log(`[${resourceType}] Loading state changed. New value:`, loading);
  }, [loading, resourceType]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Track if component is mounted to prevent state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // API function mapping (memoized to prevent recreation)
  const methods = useMemo(() => {
    const apiMethods = {
      projects: {
        getAll: api.getProjects,
        getOne: api.getProject,
        create: api.createProject,
        update: api.updateProject,
        delete: api.deleteProject,
      },
      deals: {
        getAll: api.getDeals,
        getOne: api.getDeal,
        create: api.createDeal,
        update: api.updateDeal,
        delete: api.deleteDeal,
      },
      valuations: {
        getAll: api.getValuations,
        getOne: api.getValuation,
        create: api.createValuation,
        update: api.updateValuation,
        delete: api.deleteValuation,
      },
      automations: {
        getAll: api.getAutomations,
        getOne: api.getAutomation,
        create: api.createAutomation,
        update: api.updateAutomation,
        delete: api.deleteAutomation,
      },
    };

    const resourceMethods = apiMethods[resourceType];
    if (!resourceMethods) {
      throw new Error(`Invalid resource type: ${resourceType}`);
    }
    return resourceMethods;
  }, [resourceType]);

  /**
   * Fetch all items with current parameters
   */
  const fetchItems = useCallback(
    async (newParams = null) => {
      if (!mountedRef.current) return;
      const fetchParams = newParams || params;

      setLoading(true);
      setError(null);

      try {
        const response = await methods.getAll(fetchParams);
        if (mountedRef.current) {
          setData(response);
          setItems(response[resourceType] || []);
          setMeta(response.meta || null);
          setLastFetch(new Date().toISOString());
        }

        return response;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [methods, resourceType]
  );

  // Debug when fetchItems gets recreated
  useEffect(() => {
    // console.log(`[${resourceType}] fetchItems useCallback recreated`);
  }, [fetchItems]);

  /**
   * Fetch a single item by ID
   */
  const fetchItem = useCallback(
    async (id) => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        const response = await methods.getOne(id);
        return response[resourceType.slice(0, -1)]; // Remove 's' from pluralized resource name
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [methods.getOne, resourceType]
  );

  /**
   * Create a new item
   */
  const createItem = useCallback(
    async (itemData) => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        const response = await methods.create(itemData);
        const newItem = response[resourceType.slice(0, -1)];

        // Optimistic update
        if (mountedRef.current) {
          setItems((prevItems) => [newItem, ...prevItems]);
          setMeta((prevMeta) =>
            prevMeta ? { ...prevMeta, total: prevMeta.total + 1 } : null
          );
        }

        return newItem;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [methods.create, resourceType]
  );

  /**
   * Update an existing item
   */
  const updateItem = useCallback(
    async (id, itemData) => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        const response = await methods.update(id, itemData);
        const updatedItem = response[resourceType.slice(0, -1)];

        // Optimistic update
        if (mountedRef.current) {
          setItems((prevItems) =>
            prevItems.map((item) => (item.id === id ? updatedItem : item))
          );
        }

        return updatedItem;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [methods.update, resourceType]
  );

  /**
   * Delete an item
   */
  const deleteItem = useCallback(
    async (id) => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        const response = await methods.delete(id);

        // Optimistic update
        if (mountedRef.current) {
          setItems((prevItems) => prevItems.filter((item) => item.id !== id));
          setMeta((prevMeta) =>
            prevMeta ? { ...prevMeta, total: prevMeta.total - 1 } : null
          );
        }

        return response;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [methods.delete]
  );

  /**
   * Update parameters and trigger refetch
   */
  const updateParams = useCallback(
    (newParams) => {
      const updatedParams = { ...params, ...newParams };
      setParams(updatedParams);
      return fetchItems(updatedParams);
    },
    [params] // Removed fetchItems dependency
  );

  /**
   * Clear parameters and reset to defaults
   */
  const clearParams = useCallback(() => {
    setParams(initialParams);
    return fetchItems(initialParams);
  }, [initialParams]); // Removed fetchItems dependency

  /**
   * Refresh data with current parameters
   */
  const refresh = useCallback(() => {
    api.clearCache(); // Clear cache to force fresh data
    return fetchItems();
  }, []); // Removed fetchItems dependency

  const prevParamsRef = useRef();

  // Initial fetch on mount
  useEffect(() => {
    console.log(`[${resourceType}] Initial mount - fetching items`);
    fetchItems(params);
  }, []); // Empty dependency array means this runs only on mount

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParamsRef.current)) {
      console.log(
        `[${resourceType}] useEffect detected param change. Fetching items.`
      );
      fetchItems(params);
      prevParamsRef.current = params;
    }
  }, [params, fetchItems]);

  console.log(`[${resourceType}] Returning state:`, {
    loading,
    error,
    items: items.length,
  });
  return {
    // Data
    data,
    items,
    meta,
    params,
    lastFetch,

    // State
    loading,
    error,

    // Actions
    fetchItems,
    fetchItem,
    createItem,
    updateItem,
    deleteItem,
    updateParams,
    clearParams,
    refresh,
    clearError,
  };
};

/**
 * Hook for Projects
 * Fetches all projects on mount and provides CRUD operations.
 * See useFilteredData in this file and Projects.jsx in the pages directory
 * to see how this hook is used with search and filtering.
 */
export const useProjects = (initialParams = DEFAULT_PARAMS) => {
  return useApiResource("projects", initialParams);
};

/**
 * Hook for Deals
 * Fetches all deals on mount and provides CRUD operations.
 * See useFilteredData in this file and Deals.jsx in the pages directory
 * to see how this hook is used with search and filtering.
 */
export const useDeals = (initialParams = DEFAULT_PARAMS) => {
  return useApiResource("deals", initialParams);
};

/**
 * Hook for Valuations
 * Fetches all valuations on mount and provides CRUD operations.
 * See useFilteredData in this file and Valuations.jsx in the pages directory
 * to see how this hook is used with search and filtering.
 */
export const useValuations = (initialParams = DEFAULT_PARAMS) => {
  return useApiResource("valuations", initialParams);
};

/**
 * Hook for Automations
 * Fetches all automations on mount and provides CRUD operations.
 * See useFilteredData in this file and Automations.jsx in the pages directory
 * to see how this hook is used with search and filtering.
 */
export const useAutomations = (initialParams = DEFAULT_PARAMS) => {
  return useApiResource("automations", initialParams);
};

/**
 * Hook for fetching a single project
 */
export const useProject = (id) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchProject = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const fetchedProject = await api.getProject(id);
      setProject(fetchedProject);
      return fetchedProject;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    loading,
    error,
    refresh: fetchProject,
    clearError,
  };
};

/**
 * Hook for fetching a single deal
 */
export const useDeal = (id) => {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchDeal = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const fetchedDeal = await api.getDeal(id);
      setDeal(fetchedDeal);
      return fetchedDeal;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);

  return {
    deal,
    loading,
    error,
    refresh: fetchDeal,
    clearError,
  };
};

/**
 * Hook for fetching a single valuation
 */
export const useValuation = (id) => {
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchValuation = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const fetchedValuation = await api.getValuation(id);
      setValuation(fetchedValuation);
      return fetchedValuation;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchValuation();
  }, [fetchValuation]);

  return {
    valuation,
    loading,
    error,
    refresh: fetchValuation,
    clearError,
  };
};

/**
 * Hook for fetching a single automation
 */
export const useAutomation = (id) => {
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchAutomation = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const fetchedAutomation = await api.getAutomation(id);
      setAutomation(fetchedAutomation);
      return fetchedAutomation;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAutomation();
  }, [fetchAutomation]);

  return {
    automation,
    loading,
    error,
    refresh: fetchAutomation,
    clearError,
  };
};

/**
 * Hook for dashboard statistics
 */
export const useDashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const dashboardStats = await api.getDashboardStats();
      setStats(dashboardStats);
      return dashboardStats;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
    clearError,
  };
};

/**
 * Hook for global search
 */
export const useGlobalSearch = () => {
  const [searchResults, setSearchResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const search = useCallback(
    async (term, entityTypes = ["projects", "deals", "valuations"]) => {
      setSearchTerm(term);

      if (!term || term.trim().length < 2) {
        setSearchResults(null);
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await api.globalSearch(term, entityTypes);
        setSearchResults(results);
        return results;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchResults(null);
    clearError();
  }, [clearError]);

  return {
    searchResults,
    searchTerm,
    loading,
    error,
    search,
    clearSearch,
    clearError,
  };
};

/**
 * Hook for real-time filtering with debouncing
 */
export const useFilteredData = (
  useDataHook,
  initialParams = DEFAULT_PARAMS,
  debounceMs = 300
) => {
  const [debouncedParams, setDebouncedParams] = useState(initialParams);
  const dataHook = useDataHook(debouncedParams);

  // Debounce parameter updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedParams(initialParams);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [initialParams, debounceMs]);

  return {
    ...dataHook,
    debouncedParams,
  };
};

/**
 * Hook for managing form data with API integration
 * @returns An object with form state, handlers, and API resource methods.
 */
export const useApiForm = (resourceType, itemId = null) => {
  const apiResource = useApiResource(resourceType, DEFAULT_PARAMS);

  const [initialData, setInitialData] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(!!itemId);
  const [formData, setFormData] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const apiMethods = {
    projects: { create: api.createProject, update: api.updateProject },
    deals: { create: api.createDeal, update: api.updateDeal },
    valuations: { create: api.createValuation, update: api.updateValuation },
  };

  const methods = apiMethods[resourceType];

  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  const resetForm = useCallback((data = {}) => {
    setFormData(data);
    setIsDirty(false);
  }, []);

  const saveForm = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let result;

      if (itemId) {
        result = await methods.update(itemId, formData);
      } else {
        result = await methods.create(formData);
      }

      setIsDirty(false);
      setLastSaved(new Date().toISOString());

      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [itemId, formData, methods]);

  return {
    formData,
    isDirty,
    lastSaved,
    loading,
    error,
    updateFormData,
    resetForm,
    saveForm,
    clearError,
  };
};

// Export the original useApi for backward compatibility
export default useApiResource;
